import { Button, DocumentDuplicateIcon, Menu, toaster } from 'components'
import { useCurrentUser } from 'components/providers'
import { generatePath, useNavigate } from 'react-router'
import { ITemplate } from 'types'
import { copyTemplateToWorkspace, createDoc, useEntevatePermission, useWorkspaces } from 'utils'
import { RoutePath } from '../../utils/routing'

interface Props {
  template: ITemplate
  disabled: boolean
}

export default function CopyTemplateButton({ template, disabled }: Props) {
  const navigate = useNavigate()
  const user = useCurrentUser()
  const { workspaces } = useWorkspaces('emp')
  const [entevatePermission] = useEntevatePermission()

  async function handleCopy(workspaceId: string) {
    if (workspaceId === template.workspaceId) {
      const body = { ...template, title: `${template.title} - Copy` }
      const { id } = await createDoc(user!.id, 'templates', body)
      navigate(generatePath(RoutePath.TemplateInfo, { workspaceId, id }))
    } else {
      const body = { userId: user!.id, templateId: template.id, toWorkspaceId: workspaceId }

      const templatePromise = copyTemplateToWorkspace(body).then(res => res.data)
      const toWorkspace = workspaces?.find(w => w.id === workspaceId)
      const description = `To workspace: ${toWorkspace?.title}`
      toaster.promise(templatePromise, {
        loading: { title: 'Template Copying', description },
        success: { title: 'Template Copied', description },
        error: { title: 'Template Copy Failed', description },
      })
      const { id } = await templatePromise
      navigate(generatePath(RoutePath.TemplateInfo, { workspaceId, id }))
    }
  }

  if (entevatePermission === 'entevate') {
    const options =
      workspaces
        ?.map(w => ({
          label: `${w.title}${w.id === template.workspaceId ? ' (current)' : ''}`,
          value: w.id,
        }))
        .sort((a, b) => {
          if (a.value === template.workspaceId) return -1
          if (b.value === template.workspaceId) return 1
          return a.label.localeCompare(b.label)
        }) ?? []

    return (
      <Menu
        borderRadius="none"
        buttonLabel="Copy"
        hideCheckbox
        disabled={disabled}
        leftIcon={<DocumentDuplicateIcon type="outline" />}
        onChange={handleCopy}
        options={options}
        rightIcon={undefined}
        size="sm"
        variant="ghost"
      />
    )
  } else {
    return (
      <Button
        leftIcon={<DocumentDuplicateIcon type="outline" />}
        onClick={() => handleCopy(template.workspaceId)}
        size="sm"
        variant="ghost"
        disabled={disabled}
      >
        Copy
      </Button>
    )
  }
}
